import { lazy } from "react";

import type { PermissionRule } from "../domain/permissions";
import { ModuleGate } from "../shell/ModuleGate";
import { PlatformRoute, type LazyComponent } from "../shell/PlatformRoute";
import { modules } from "../tenants";

/**
 * One routed screen. `permission` is REQUIRED on every entry — an entry with
 * no rule is a type error, not an open door.
 */
export interface RouteEntry {
  /** The canonical path from SPEC-APP.md §1, in `:param` form. */
  path: string;
  title: string;
  desktop: LazyComponent;
  tablet?: LazyComponent;
  phone?: LazyComponent;
  permission: PermissionRule;
  /** Set on customer-module routes; Phase 5 moves these into the descriptor. */
  module?: string;
  /** The server navigation string this entry is revealed by, when it has one. */
  navKey?: string;
}

/**
 * THE ONE ROUTE TABLE — data, not JSX. Copied route for route from
 * PROJECT-KNOWLEDGE.md §Routes and posture, which is the closed map.
 *
 * The table is COMPLETE: a route with no page yet still has an entry, because
 * omitting it is what makes /dev/coverage lie.
 *
 * Variant imports are lazy and platform-keyed, so a build never fetches the
 * tablet or phone chunk of a route that declares neither.
 */
export const coreRoutes: RouteEntry[] = [
  // Sign-in is a real page (decision F-D3): one deployment serves every tenant, so `/login` is
  // where a user says who they are before anything can know which tenant to ask about. It is
  // the only way in (AU-D14): the dev persona picker that used to sit inside it is gone.
  {
    path: "/login",
    title: "Sign in",
    desktop: lazy(() => import("../pages/desktop/Login")),
    tablet: lazy(() => import("../pages/tablet/Login")),
    phone: lazy(() => import("../pages/phone/Login")),
    permission: "none",
  },
  // The identity provider's way back (AU-D13): reads the redirect, accepts open invitations,
  // then chooses the tenant. Every platform has a variant so a phone never sees the
  // "not optimised" notice on a screen that is only a status line.
  {
    path: "/auth/callback",
    title: "Signing in",
    desktop: lazy(() => import("../pages/desktop/AuthCallback")),
    tablet: lazy(() => import("../pages/tablet/AuthCallback")),
    phone: lazy(() => import("../pages/phone/AuthCallback")),
    permission: "none",
  },
  {
    path: "/",
    title: "Home",
    desktop: lazy(() => import("../pages/desktop/Home")),
    tablet: lazy(() => import("../pages/tablet/Home")),
    phone: lazy(() => import("../pages/phone/Home")),
    permission: "none",
  },
  {
    path: "/data",
    title: "Data overview",
    desktop: lazy(() => import("../pages/desktop/DataOverview")),
    permission: {
      anyOf: [
        "sites:read",
        "products:read",
        "lane-capacities:read",
        "modes:read",
        "carriers:read",
        "calendars:read",
        "priority-policies:read",
        "solver-config:read",
        "fx-rates:read",
        "demand:read",
        "deliveries:read",
      ],
    },
  },
  {
    path: "/data/feed-driven",
    title: "Feed-driven data",
    desktop: lazy(() => import("../pages/desktop/FeedDrivenData")),
    permission: {
      anyOf: ["sites:read", "products:read", "calendars:read", "demand:read", "deliveries:read"],
    },
  },
  {
    path: "/data/manually-managed",
    title: "Manually managed data",
    desktop: lazy(() => import("../pages/desktop/ManuallyManagedData")),
    permission: {
      anyOf: [
        "lane-capacities:read",
        "fx-rates:read",
        "modes:read",
        "carriers:read",
        "priority-policies:read",
        "solver-config:read",
      ],
    },
  },
  /* The index is declared BEFORE the child so `:issueId` never shadows the list. */
  {
    path: "/planning/issues",
    title: "Planning issues",
    desktop: lazy(() => import("../pages/desktop/PlanningIssues")),
    permission: "runs:read",
  },
  {
    path: "/planning/issues/:issueId",
    title: "Planning issue",
    desktop: lazy(() => import("../pages/desktop/PlanningIssueDetail")),
    permission: "runs:read",
  },
  {
    path: "/data/issues",
    title: "Data feed issues",
    desktop: lazy(() => import("../pages/desktop/DataIssues")),
    permission: "deliveries:read",
  },
  {
    path: "/data/issues/:issueId",
    title: "Data feed issue",
    desktop: lazy(() => import("../pages/desktop/DataIssueDetail")),
    permission: "deliveries:read",
  },
  {
    path: "/sites",
    title: "Sites",
    desktop: lazy(() => import("../pages/desktop/Sites")),
    permission: "sites:read",
  },
  /* The index is declared BEFORE the child so `:siteId` never shadows the hub. */
  {
    path: "/site-workspace",
    title: "Site workspace",
    desktop: lazy(() => import("../pages/desktop/SiteWorkspaceSearch")),
    permission: "sites:read",
  },
  {
    path: "/site-workspace/:siteId",
    title: "Site workspace detail",
    desktop: lazy(() => import("../pages/desktop/SiteWorkspace")),
    permission: "sites:read",
  },
  /* The index is declared BEFORE the child so `:laneId` never shadows the hub. */
  {
    path: "/lane-workspace",
    title: "Lane workspace",
    desktop: lazy(() => import("../pages/desktop/LaneWorkspaceSearch")),
    permission: "lane-capacities:read",
  },
  {
    path: "/lane-workspace/:laneId",
    title: "Lane workspace detail",
    desktop: lazy(() => import("../pages/desktop/LaneWorkspace")),
    permission: "lane-capacities:read",
  },
  {
    path: "/products",
    title: "Products",
    desktop: lazy(() => import("../pages/desktop/Products")),
    permission: "products:read",
  },
  {
    path: "/lane-capacities",
    title: "Lane rates",
    desktop: lazy(() => import("../pages/desktop/LaneRates")),
    permission: "lane-capacities:read",
  },
  /* Exchange rates (AN-D17, PM-D22): what a carrier rate in another currency is converted with. */
  {
    path: "/fx-rates",
    title: "Exchange rates",
    desktop: lazy(() => import("../pages/desktop/FxRates")),
    permission: "fx-rates:read",
  },
  {
    path: "/modes",
    title: "Modes",
    desktop: lazy(() => import("../pages/desktop/Modes")),
    permission: "modes:read",
  },
  {
    path: "/carriers",
    title: "Carriers",
    desktop: lazy(() => import("../pages/desktop/Carriers")),
    permission: "carriers:read",
  },
  {
    path: "/calendars",
    title: "Site closures",
    desktop: lazy(() => import("../pages/desktop/SiteClosures")),
    tablet: lazy(() => import("../pages/tablet/Calendars")),
    phone: lazy(() => import("../pages/phone/Calendars")),
    permission: "calendars:read",
  },
  {
    path: "/priority-policies",
    title: "Priority policies",
    desktop: lazy(() => import("../pages/desktop/PriorityPolicies")),
    permission: "priority-policies:read",
  },
  {
    path: "/solver-configuration",
    title: "Solver configuration",
    desktop: lazy(() => import("../pages/desktop/SolverConfiguration")),
    permission: "solver-config:read",
  },
  {
    path: "/uploads",
    title: "Uploads",
    desktop: lazy(() => import("../pages/desktop/Uploads")),
    permission: { anyOf: ["sites:read", "products:read", "lane-capacities:read"] },
  },
  {
    path: "/uploads/:uploadId",
    title: "Upload detail",
    desktop: lazy(() => import("../pages/desktop/UploadDetail")),
    permission: { anyOf: ["sites:read", "products:read", "lane-capacities:read"] },
  },
  {
    path: "/demand/transfer-requests",
    title: "Transfer requests",
    desktop: lazy(() => import("../pages/desktop/TransferRequests")),
    permission: "demand:read",
  },
  {
    path: "/demand/booked-loads",
    title: "Booked loads",
    desktop: lazy(() => import("../pages/desktop/BookedLoads")),
    permission: "demand:read",
  },
  {
    path: "/demand/reserved-capacity",
    title: "Reserved capacity",
    desktop: lazy(() => import("../pages/desktop/ReservedCapacity")),
    permission: "demand:read",
  },
  {
    path: "/outbound",
    title: "Outbound channels",
    desktop: lazy(() => import("../pages/desktop/Outbound")),
    permission: "outbound:read",
  },
  {
    path: "/outbound/snapshots/:snapshotId",
    title: "Outbound snapshot",
    desktop: lazy(() => import("../pages/desktop/OutboundSnapshot")),
    permission: "outbound:read",
  },
  {
    path: "/runs",
    title: "Planning runs",
    desktop: lazy(() => import("../pages/desktop/PlanningRuns")),
    tablet: lazy(() => import("../pages/tablet/Runs")),
    phone: lazy(() => import("../pages/phone/Runs")),
    permission: "runs:read",
  },
  {
    path: "/runs/:runId",
    title: "Run detail",
    desktop: lazy(() => import("../pages/desktop/RunDetail")),
    tablet: lazy(() => import("../pages/tablet/RunDetail")),
    phone: lazy(() => import("../pages/phone/RunDetail")),
    permission: "runs:read",
  },
  /* Experiments — scenarios (W4-2, SN-D16): the real scenario API behind the screens. Every route
     reads with `scenarios:read`; the controls that write or promote are gated on `scenarios:write`
     and `scenarios:promote` inside the pages. */
  {
    path: "/experiments",
    title: "Experiments",
    desktop: lazy(() => import("../pages/desktop/Experiments")),
    permission: "scenarios:read",
  },
  {
    path: "/experiments/compare",
    title: "Compare scenarios",
    desktop: lazy(() => import("../pages/desktop/ExperimentCompare")),
    permission: "scenarios:read",
  },
  {
    path: "/experiments/:scenarioId",
    title: "Scenario",
    desktop: lazy(() => import("../pages/desktop/ExperimentScenario")),
    permission: "scenarios:read",
  },
  /* The reports hub: every report is a tab here. The per-report paths below stay routable so
     existing links keep working. */
  {
    path: "/reports",
    title: "Reports",
    desktop: lazy(() => import("../pages/desktop/ReportsWorkspace")),
    permission: "reports:read",
  },
  {
    path: "/reports/lane-detail",
    title: "Lane detail report",
    desktop: lazy(() => import("../pages/desktop/LaneDetailReport")),
    permission: "reports:read",
  },
  {
    path: "/reports/product-detail",
    title: "Product detail report",
    desktop: lazy(() => import("../pages/desktop/ProductDetailReport")),
    permission: "reports:read",
  },
  {
    path: "/reports/inbound-lane-detail",
    title: "Inbound lane detail report",
    desktop: lazy(() => import("../pages/desktop/InboundLaneDetailReport")),
    permission: "reports:read",
  },
  {
    path: "/reports/lane-days-drift",
    title: "Lane-days drift report",
    desktop: lazy(() => import("../pages/desktop/LaneDaysDriftReport")),
    permission: "reports:read",
  },
  {
    path: "/reports/map",
    title: "Network map",
    desktop: lazy(() => import("../pages/desktop/NetworkMap")),
    tablet: lazy(() => import("../pages/tablet/NetworkMap")),
    permission: "reports:read",
  },
  {
    path: "/deliveries",
    title: "Data feed log",
    desktop: lazy(() => import("../pages/desktop/Deliveries")),
    tablet: lazy(() => import("../pages/tablet/Deliveries")),
    phone: lazy(() => import("../pages/phone/Deliveries")),
    permission: "deliveries:read",
  },
  {
    path: "/deliveries/:deliveryId",
    title: "Feed delivery detail",
    desktop: lazy(() => import("../pages/desktop/DeliveryDetail")),
    phone: lazy(() => import("../pages/phone/DeliveryDetail")),
    permission: "deliveries:read",
  },
  {
    path: "/audit",
    title: "Application logs",
    desktop: lazy(() => import("../pages/desktop/AuditLog")),
    permission: "audit:read",
  },
  {
    path: "/audit/ui-edit",
    title: "UI edits",
    desktop: lazy(() => import("../pages/desktop/AuditTrail")),
    permission: "audit:read",
  },
  {
    /* A direct route (PS-D12): in the admin navigation only when the tenant has several scopes. */
    path: "/admin/planning-scopes",
    title: "Planning scopes",
    desktop: lazy(() => import("../pages/desktop/PlanningScopes")),
    permission: "planning-scopes:write",
  },
  /* Members and invitations (AU-D12, `design-docs/entra-sign-in.md` §5): who may use this tenant. */
  {
    path: "/admin/members",
    title: "Members",
    desktop: lazy(() => import("../pages/desktop/Members")),
    permission: "tenant:admin",
  },
  {
    path: "/admin/queue",
    title: "Queue stats",
    desktop: lazy(() => import("../pages/desktop/QueueStats")),
    permission: "queue:read",
  },
  {
    path: "/analytics/spend",
    title: "Spend",
    desktop: lazy(() => import("../pages/desktop/AnalyticsSpend")),
    tablet: lazy(() => import("../pages/tablet/AnalyticsSpend")),
    phone: lazy(() => import("../pages/phone/AnalyticsSpend")),
    permission: "analytics:read",
  },
  {
    path: "/analytics/savings",
    title: "Savings",
    desktop: lazy(() => import("../pages/desktop/AnalyticsSavings")),
    tablet: lazy(() => import("../pages/tablet/AnalyticsSavings")),
    phone: lazy(() => import("../pages/phone/AnalyticsSavings")),
    permission: "analytics:read",
  },
  {
    path: "/analytics/findings",
    title: "Plan health",
    desktop: lazy(() => import("../pages/desktop/AnalyticsFindings")),
    tablet: lazy(() => import("../pages/tablet/AnalyticsFindings")),
    phone: lazy(() => import("../pages/phone/AnalyticsFindings")),
    permission: "analytics:read",
  },
  {
    path: "/analytics/explain",
    title: "Explain",
    desktop: lazy(() => import("../pages/desktop/AnalyticsExplain")),
    permission: "analytics:read",
  },
  {
    path: "/analytics/compare",
    title: "Compare runs",
    desktop: lazy(() => import("../pages/desktop/AnalyticsCompare")),
    tablet: lazy(() => import("../pages/tablet/AnalyticsCompare")),
    permission: "analytics:read",
  },
  {
    path: "/dev/coverage",
    title: "Route coverage",
    desktop: lazy(() => import("../pages/desktop/DevCoverage")),
    permission: "none",
  },
  {
    path: "*",
    title: "Page not found",
    desktop: lazy(() => import("../pages/desktop/NotFound")),
    permission: "none",
  },
];

/** Core plus every registered module's routes. Still one filterable array of data. */
export const routes: RouteEntry[] = [...coreRoutes, ...modules.flatMap((m) => m.routes)];

const byPath = new Map(routes.map((r) => [r.path, r]));

/** Look an entry up without throwing — for paths that exist only under a build flag. */
export function findRouteEntry(path: string): RouteEntry | undefined {
  return byPath.get(path);
}

/** Look an entry up by its canonical path; unknown paths throw at module load. */
export function routeEntry(path: string): RouteEntry {
  const entry = byPath.get(path);
  if (!entry) throw new Error(`No route table entry for ${path}`);
  return entry;
}

/**
 * Renders one entry: the platform variant, wrapped in `ModuleGate` when the entry belongs
 * to a customer module. Every thin route file under `src/routes/` renders this.
 */
export function RoutePage({ entry }: { entry: RouteEntry }) {
  const page = (
    <PlatformRoute
      desktop={entry.desktop}
      {...(entry.tablet ? { tablet: entry.tablet } : {})}
      {...(entry.phone ? { phone: entry.phone } : {})}
    />
  );
  if (!entry.module) return page;
  return <ModuleGate moduleKey={entry.module}>{page}</ModuleGate>;
}
